import { useState } from 'react'
import { Check, LayoutGrid, Send, Search } from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Investor Deck v4 (/deck4)
   Simplified version of Deck 3: same structure, less text, cleaner layout.
   Two-column: copy left, mocks right. Underlined highlights like deck2.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function Deck4Page() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-6 right-8 z-50">
        <span className="text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide { title: string; component: React.FC }

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

function SlideLayout({
  tag,
  headline,
  highlight,
  subline,
  bullets,
  children,
}: {
  tag?: string
  headline: string
  highlight?: string
  subline?: string
  bullets?: string[]
  children: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          {headline}
          {highlight && <span className="text-[#FF2300] underline decoration-[#FF2300]/60 underline-offset-2">{highlight}</span>}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400">{subline}</p>}
        {bullets && bullets.length > 0 && (
          <ul className="mt-6 space-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
                <span className="text-zinc-300 text-[15px]">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="relative flex items-center justify-center min-h-[280px] lg:min-h-[340px]">
        {children}
      </div>
    </div>
  )
}

function MockCard({ className = '', glow, children }: { className?: string; glow?: boolean; children: React.ReactNode }) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-white/[0.06] ${glow ? 'shadow-[0_0_60px_-12px_rgba(255,35,0,0.2)]' : ''} ${className}`}>
      {children}
    </div>
  )
}

function MockHeroOverlap() {
  return (
    <div className="relative w-full max-w-xl mx-auto flex items-center justify-center">
      <MockCard className="absolute right-0 top-0 w-[72%] max-w-md p-5 opacity-90" glow>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white">Team Management</span>
          <LayoutGrid className="w-4 h-4 text-zinc-500" />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg bg-white/5 p-2"><span className="text-lg font-semibold text-white">42</span><span className="text-[11px] text-zinc-500 block">Shows</span></div>
          <div className="rounded-lg bg-white/5 p-2"><span className="text-lg font-semibold text-white">53</span><span className="text-[11px] text-zinc-500 block">Tasks</span></div>
        </div>
        <div className="space-y-1">
          {['Charly Manager', 'Promoter'].map((n, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5">
              <div className="w-7 h-7 rounded-full bg-white/10" />
              <span className="text-[12px] text-zinc-300">{n}</span>
            </div>
          ))}
        </div>
      </MockCard>
      <MockCard className="relative left-0 z-10 w-[65%] max-w-sm p-4" glow>
        <div className="flex gap-3 mb-2 border-b border-white/10 pb-2">
          <span className="text-[13px] font-medium text-[#FF2300] border-b-2 border-[#FF2300]">Shows</span>
          <span className="text-[13px] text-zinc-500">Schedule</span>
        </div>
        <div className="text-[11px] text-zinc-500">UPCOMING · 31 · SHOWS · 19</div>
        <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 mt-2 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[12px] text-zinc-500">Ask anything...</span>
          <Send className="w-3.5 h-3.5 text-[#FF2300] ml-auto" />
        </div>
      </MockCard>
    </div>
  )
}

const TOOLS = ['Email', 'iMessage', 'Spreadsheets', 'Slack', 'Asana', 'MasterTour', 'Spotify']
function MockFragmented() {
  return (
    <MockCard className="w-full max-w-sm p-4" glow>
      <p className="text-[11px] text-zinc-500 mb-3">Fragmented — no connection</p>
      <div className="flex flex-wrap gap-2">
        {TOOLS.map((t, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-[12px] text-zinc-400">{t}</div>
        ))}
      </div>
    </MockCard>
  )
}

function MockChatBubbles() {
  const msgs = [
    { from: 'Manager', text: 'Did we get the splits sorted for the remix?', time: '2:14 PM' },
    { from: 'Artist', text: 'Which remix? Last month or the new one?', time: '2:18 PM' },
    { from: 'Manager', text: 'Splits were in a Google doc somewhere...', time: '2:22 PM' },
    { from: 'Booking Agent', text: 'Need to confirm Denver by EOD. Routing finalized?', time: '3:05 PM' },
  ]
  return (
    <MockCard className="w-full max-w-md p-4" glow>
      <p className="text-[11px] text-zinc-500 mb-3">iMessage · Email · Slack</p>
      <div className="space-y-2">
        {msgs.map((m, i) => (
          <div key={i} className="rounded-xl px-3 py-2 bg-white/5 border border-white/10">
            <div className="flex justify-between mb-0.5">
              <span className="text-[10px] font-semibold text-zinc-500">{m.from}</span>
              <span className="text-[10px] text-zinc-600">{m.time}</span>
            </div>
            <p className="text-[12px] text-zinc-400">&ldquo;{m.text}&rdquo;</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 mt-2">Nothing tracked. Nothing connected.</p>
    </MockCard>
  )
}

function MockCentralDizzy() {
  return (
    <MockCard className="w-full max-w-sm p-4" glow>
      <p className="text-[11px] text-zinc-500 mb-3">One system — everything connected</p>
      <div className="flex flex-wrap gap-2 justify-center mb-4">
        {TOOLS.slice(0, 5).map((t, i) => (
          <div key={i} className="rounded border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-zinc-500">{t}</div>
        ))}
      </div>
      <div className="rounded-xl border-2 border-[#FF2300]/40 bg-[#FF2300]/10 p-4 flex items-center justify-center gap-2">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm" />
        <span className="text-sm font-semibold text-white">DIZEE</span>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3 text-center">Schedule · Shows · Tasks · Music · Team</p>
    </MockCard>
  )
}

function MockTicketCounts() {
  return (
    <MockCard className="w-full max-w-sm p-4" glow>
      <h3 className="text-sm font-semibold text-white mb-3">Update Ticket Counts</h3>
      <div className="rounded-lg bg-white/5 p-2.5 mb-2">
        <div className="flex justify-between text-[13px] font-medium text-white">Tempo <span className="text-[#FF2300]">82%</span></div>
        <div className="text-[11px] text-zinc-500">Cap 400 · Sold 328</div>
        <div className="mt-1 h-1 rounded-full bg-white/10"><div className="h-full rounded-full bg-[#FF2300] w-[82%]" /></div>
      </div>
      <div className="rounded-lg bg-white/5 p-2.5">
        <div className="flex justify-between text-[13px] font-medium text-white">Stage West <span className="text-[#FF2300]">76%</span></div>
        <div className="text-[11px] text-zinc-500">Cap 1,200 · Sold 915</div>
        <div className="mt-1 h-1 rounded-full bg-white/10"><div className="h-full rounded-full bg-[#FF2300] w-[76%]" /></div>
      </div>
    </MockCard>
  )
}

function MockMarketSegments() {
  const segs = [
    { label: 'TAM', stat: '$7.5B', sub: 'Creator infrastructure' },
    { label: 'SAM', stat: '$750M', sub: 'Music wedge' },
    { label: 'SOM', stat: '$15–20M', sub: 'Professional teams (ARR)' },
  ]
  return (
    <div className="grid grid-cols-3 gap-2 w-full max-w-lg">
      {segs.map((s, i) => (
        <MockCard key={i} className="p-4" glow={i === 2}>
          <p className="text-[11px] font-semibold text-[#FF2300] uppercase">{s.label}</p>
          <p className="text-xl font-bold text-white mt-1">{s.stat}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">{s.sub}</p>
        </MockCard>
      ))}
    </div>
  )
}

function MockPricing() {
  return (
    <div className="grid grid-cols-3 gap-2 w-full max-w-2xl">
      {[
        { tier: 'Artist', price: '$45', note: '/mo' },
        { tier: 'Artist Pro', price: '$75', note: '/mo', popular: true },
        { tier: 'Enterprise', price: '$125', note: '/mo' },
      ].map((p, i) => (
        <MockCard key={i} className={`p-4 ${p.popular ? 'ring-1 ring-[#FF2300]/30' : ''}`} glow={p.popular}>
          <p className="text-[12px] font-semibold text-white">{p.tier}</p>
          {p.popular && <span className="text-[9px] text-[#FF2300] font-semibold">Popular</span>}
          <p className="text-lg font-bold text-white mt-1">{p.price}<span className="text-[11px] text-zinc-500 font-normal ml-0.5">{p.note}</span></p>
        </MockCard>
      ))}
    </div>
  )
}

function MockGTM() {
  const phases = [
    { phase: 'Phase 1', title: 'Founder-Led', target: 'First 200' },
    { phase: 'Phase 2', title: 'Signal Amplification', target: '500–700' },
    { phase: 'Phase 3', title: 'Scalable Acquisition', target: '1,500+' },
  ]
  return (
    <div className="grid grid-cols-3 gap-3 w-full max-w-xl">
      {phases.map((p, i) => (
        <MockCard key={i} className="p-4">
          <p className="text-[10px] text-[#FF2300]/80 font-mono">{p.phase}</p>
          <p className="text-sm font-semibold text-white mt-1">{p.title}</p>
          <p className="text-[11px] text-zinc-500 mt-2">Target: <span className="text-white font-medium">{p.target}</span></p>
        </MockCard>
      ))}
    </div>
  )
}

function MockCompetition() {
  const dots = [
    { name: 'Venice', x: 18, y: 22 },
    { name: 'Artist Growth', x: 42, y: 28 },
    { name: 'Notion', x: 58, y: 70 },
    { name: 'DIZEE', x: 88, y: 90, highlight: true },
  ]
  return (
    <MockCard className="w-full max-w-md p-4" glow>
      <p className="text-[11px] text-zinc-500 mb-2">X: Static → 360° · Y: Disconnected → Intelligent</p>
      <div className="relative h-48 rounded-lg bg-white/5 border border-white/10">
        {dots.map((d, i) => (
          <div key={i} className={`absolute text-[11px] font-semibold ${d.highlight ? 'text-[#FF2300]' : 'text-zinc-500'}`} style={{ left: `${d.x}%`, top: `${d.y}%`, transform: 'translate(-50%,-50%)' }}>{d.name}</div>
        ))}
      </div>
    </MockCard>
  )
}

function MockTeam() {
  const team = [
    { name: 'Ryan Thomas', role: 'CEO' },
    { name: 'Edward Contreras', role: 'CTO' },
    { name: 'Aaron P. Wong', role: 'COO' },
    { name: 'Owais Ghaus', role: 'CMO' },
    { name: 'Christopher Alba', role: 'CBO' },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full max-w-xl">
      {team.map((t, i) => (
        <MockCard key={i} className="p-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#FF2300]/20 border border-white/10 flex items-center justify-center">
              <span className="text-[11px] font-semibold text-white">{t.name.split(' ').map((n) => n[0]).join('')}</span>
            </div>
            <div>
              <p className="text-[13px] font-semibold text-white">{t.name}</p>
              <p className="text-[11px] text-[#FF2300]">{t.role}</p>
            </div>
          </div>
        </MockCard>
      ))}
    </div>
  )
}

function MockUseOfFunds() {
  const items = ['Product–market fit', 'Core platform', 'Integrations', 'Marquee onboarding', 'Team', 'Infrastructure']
  return (
    <MockCard className="w-full max-w-md p-4" glow>
      <h3 className="text-sm font-semibold text-white mb-3">Use of funds</h3>
      <div className="grid grid-cols-2 gap-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg bg-white/5 px-2 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" />
            <span className="text-[12px] text-zinc-300">{item}</span>
          </div>
        ))}
      </div>
    </MockCard>
  )
}

function MockProductPills() {
  const pills = ['AI Assistant', 'Dashboard', 'Teams', 'Catalog', 'Bookings', 'Tour Manager', 'Tasks', '24/7 Sync']
  return (
    <MockCard className="w-full max-w-lg p-4" glow>
      <p className="text-[11px] text-zinc-500 mb-3">Unified platform</p>
      <div className="flex flex-wrap gap-2">
        {pills.map((p, i) => (
          <span key={i} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-300">{p}</span>
        ))}
      </div>
    </MockCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES (simplified copy)
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-4 w-auto text-white" />
        </div>
        <div className="flex items-center gap-3 mt-6">
          <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-1.5 text-[12px] font-medium text-[#FF2300]">Pre-Seed</span>
          <span className="text-[13px] text-zinc-500">February 2026</span>
        </div>
        <p className="text-sm text-zinc-500 mt-8">www.dizee.com</p>
      </div>
      <div className="flex items-center justify-center min-h-[320px]">
        <MockHeroOverlap />
      </div>
    </div>
  )
}

function SlideMission() {
  return (
    <SlideLayout
      tag="Mission"
      headline="The new operating system and "
      highlight="source of truth"
      subline="So artists, managers, and their teams stay aligned."
      bullets={['One place for the whole career.', 'Real-time clarity. Better decisions.']}
    >
      <MockCentralDizzy />
    </SlideLayout>
  )
}

function SlideWhatsHappening() {
  return (
    <SlideLayout
      tag="What's Happening Now"
      headline="The industry has shifted. "
      highlight="Fundamentally."
      bullets={[
        'Labels no longer manage most careers.',
        'Independent artists and small teams run their own businesses.',
        'Infrastructure never followed.',
      ]}
    >
      <MockFragmented />
    </SlideLayout>
  )
}

function SlideIndustryTrends() {
  return (
    <SlideLayout
      tag="Industry Trends"
      headline="What's coming next."
      subline="The industry has changed. The tools haven't."
      bullets={[
        '75.9M creators (2023) → 198.2M by 2030.',
        '120K+ new tracks per day. Creator economy $252B+.',
        'No unified system exists.',
      ]}
    >
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
        <MockCard className="p-4"><p className="text-2xl font-bold text-[#FF2300]">75.9M</p><p className="text-[11px] text-zinc-500">Creators (2023)</p></MockCard>
        <MockCard className="p-4"><p className="text-2xl font-bold text-emerald-400">198.2M</p><p className="text-[11px] text-zinc-500">By 2030</p></MockCard>
      </div>
    </SlideLayout>
  )
}

function SlideProblemCurrentTools() {
  return (
    <SlideLayout
      tag="The Problem — Current State"
      headline="Music business is still "
      highlight="outdated."
      bullets={[
        'Manual processes. Nothing connected.',
        'Fragmented communication — texts, email, spreadsheets.',
        'Legacy tools. Disconnected systems.',
      ]}
    >
      <MockFragmented />
    </SlideLayout>
  )
}

function SlideProblemHighLevel() {
  return (
    <SlideLayout
      tag="The Problem"
      headline="Artists lack real-time visibility "
      highlight="into what's happening in their career."
      bullets={[
        'Managers lack a centralized system and reliable data.',
        'Tools are slow and fragmented. Opportunities pass.',
        'Communication is chaotic. Nothing tracked or searchable.',
      ]}
    >
      <MockChatBubbles />
    </SlideLayout>
  )
}

function SlideExampleMessages() {
  return (
    <SlideLayout
      tag="Real Conversations"
      headline="This is what managing a music career looks like today."
      subline="Scattered across iMessage, email, Slack. Nothing tracked. Nothing connected."
    >
      <MockChatBubbles />
    </SlideLayout>
  )
}

function SlideCentralizes() {
  return (
    <SlideLayout
      tag="The Solution"
      headline="A single operating system and source of truth "
      highlight="for modern music careers."
      subline="Real-time clarity. Move faster. Capture revenue as it happens."
      bullets={[
        'Unified view of releases, shows, finances, team.',
        'AI surfaces opportunities as they happen.',
        'Catalog, touring, team — all connected.',
      ]}
    >
      <div className="space-y-4">
        <MockTicketCounts />
        <MockCentralDizzy />
      </div>
    </SlideLayout>
  )
}

function SlideProduct() {
  return (
    <SlideLayout
      tag="Product"
      headline="A unified platform that centralizes your career."
      subline="Dashboard, teams, catalog, bookings, tour manager, tasks, 24/7 sync — and AI that answers anything."
    >
      <MockProductPills />
    </SlideLayout>
  )
}

function SlideMarketSize() {
  return (
    <SlideLayout
      tag="Market Size"
      headline="A massive, underserved market "
      highlight="growing every year."
      bullets={[
        'TAM: Creator infrastructure — 3% of $252B.',
        'SAM: Music wedge — ~13M artists.',
        'SOM: Professional teams — $15–20M ARR.',
      ]}
    >
      <MockMarketSegments />
    </SlideLayout>
  )
}

function SlideBusinessModel() {
  return (
    <SlideLayout
      tag="Business Model"
      headline="Simple subscription. "
      highlight="Predictable revenue."
      subline="Artist · Artist Pro · Enterprise. Plus: AI credits, add-ons, integrations."
    >
      <MockPricing />
    </SlideLayout>
  )
}

function SlideGTM() {
  return (
    <SlideLayout
      tag="Go-To-Market"
      headline="Three phases. "
      highlight="Founder-led to scalable."
      bullets={[
        'Phase 1: Founder-led — first 200 accounts.',
        'Phase 2: Signal amplification — 500–700, case studies, referrals.',
        'Phase 3: Scalable acquisition — 1,500+, content, partnerships, API.',
      ]}
    >
      <MockGTM />
    </SlideLayout>
  )
}

function SlideCompetition() {
  return (
    <SlideLayout
      tag="Competition"
      headline="From static software "
      highlight="to an intelligent operating system."
      subline="DIZEE: 360° artist career, intelligent, connected."
    >
      <MockCompetition />
    </SlideLayout>
  )
}

function SlideAdvantages() {
  return (
    <SlideLayout
      tag="Why DIZEE Wins"
      headline="Why DIZEE wins."
      bullets={[
        '2x Grammy-nominated manager — 2B+ streams.',
        'CEO with proven credibility and Billboard-charting label.',
        'Hybrid team: music insiders + product builders.',
        'DIZEE AI informed by 15+ years — music-native.',
        'First-mover. Defining the space.',
      ]}
    >
      <MockHeroOverlap />
    </SlideLayout>
  )
}

function SlideTeam() {
  return (
    <SlideLayout
      tag="Team"
      headline="The team building this."
      subline="Ryan Thomas (CEO) · Edward Contreras (CTO) · Aaron P. Wong (COO) · Owais Ghaus (CMO) · Christopher Alba (CBO)."
    >
      <MockTeam />
    </SlideLayout>
  )
}

function SlideAsk() {
  return (
    <SlideLayout
      tag="The Ask"
      headline="Raising "
      highlight="$2.5M"
      subline="Product–market fit, core platform, integrations, marquee onboarding, team, infrastructure."
      bullets={['Product–market fit with real artists and managers.', 'Core platform and deep integrations.', 'Marquee onboarding and team execution.']}
    >
      <MockUseOfFunds />
    </SlideLayout>
  )
}

function SlideClosing() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-4 w-auto text-white" />
        </div>
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug">
          The new operating system and source of truth for modern music careers.
        </p>
        <div className="mt-12 rounded-xl border-2 border-[#FF2300]/30 bg-[#FF2300]/[0.08] p-6 w-full">
          <p className="text-lg font-semibold text-white">DIZEE is building the modern operating system the music industry has been missing.</p>
        </div>
        <div className="mt-8 space-y-2">
          <p className="text-[14px] text-[#FF2300] font-medium">partners@dizee.com</p>
          <p className="text-[13px] text-zinc-500">www.dizee.com</p>
        </div>
      </div>
      <div className="flex items-center justify-center">
        <MockCentralDizzy />
      </div>
    </div>
  )
}

function SlideAppendix() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-[11px] font-semibold text-zinc-700 uppercase tracking-[0.3em]">Appendix</p>
      <div className="mt-4 w-16 h-px bg-zinc-800" />
    </div>
  )
}

function SlideWhyNow() {
  return (
    <SlideLayout
      tag="Why Now"
      headline="A generational shift. "
      highlight="The timing is now."
      bullets={[
        'Independent artists fastest-growing. Non-major labels 29.7% share.',
        'DIY creators expect label-grade tools without label control.',
        'More music in one day than all of 1989.',
        'AI and APIs matured. Building a career OS is finally possible.',
      ]}
    >
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
        <MockCard className="p-4"><p className="text-2xl font-bold text-[#FF2300]">75.9M</p><p className="text-[11px] text-zinc-500">Creators (2023)</p></MockCard>
        <MockCard className="p-4"><p className="text-2xl font-bold text-emerald-400">198.2M</p><p className="text-[11px] text-zinc-500">By 2030</p></MockCard>
      </div>
    </SlideLayout>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Mission', component: SlideMission },
  { title: "What's Happening Now", component: SlideWhatsHappening },
  { title: 'Industry Trends', component: SlideIndustryTrends },
  { title: 'Current State', component: SlideProblemCurrentTools },
  { title: 'Problem', component: SlideProblemHighLevel },
  { title: 'Real Conversations', component: SlideExampleMessages },
  { title: 'Solution', component: SlideCentralizes },
  { title: 'Product', component: SlideProduct },
  { title: 'Market Size', component: SlideMarketSize },
  { title: 'Business Model', component: SlideBusinessModel },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'Competition', component: SlideCompetition },
  { title: 'Why DIZEE Wins', component: SlideAdvantages },
  { title: 'Team', component: SlideTeam },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing', component: SlideClosing },
  { title: 'Appendix', component: SlideAppendix },
  { title: 'Why Now', component: SlideWhyNow },
]
